import { useCallback } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type {
  BulkResolveAttendeesRowResult,
  BulkResolveAttendeesRowStatus,
} from "@event-planner/shared";
import {
  attendeeImportSpec,
  type ParsedAttendeeImportRow,
} from "@event-planner/shared/imports";
import {
  ResolveAndImportDialog,
  type ProposalRow,
} from "../../components/imports/ResolveAndImportDialog";
import { type BulkImportResultBucket } from "../../components/BulkImportResultTable";
import {
  bulkInviteToEvent,
  bulkResolveAttendees,
} from "../../services/userManagementService";

interface ExcelAttendeeImportDialogProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** Fires after a non-zero import run completes so the parent can refetch. */
  onImported?: () => void;
}

type AttendeeProposal = ProposalRow<
  ParsedAttendeeImportRow,
  BulkResolveAttendeesRowResult
>;

type WriteResult = Awaited<ReturnType<typeof bulkInviteToEvent>>;

/**
 * Default include + locked policy. Pre-checks 'new' rows; locks
 * already_member / already_invited / not_found / failed off (organizer
 * sees them in the review surface but can't accidentally re-invite).
 */
export function defaultIncludeForAttendeeStatus(
  status: BulkResolveAttendeesRowStatus | "invalid",
): { include: boolean; locked: boolean } {
  switch (status) {
    case "new":
      return { include: true, locked: false };
    case "already_member":
    case "already_invited":
    case "not_found":
    case "failed":
    case "invalid":
      return { include: false, locked: true };
  }
}

/**
 * Email-only Excel bulk add for the Attendees page, wrapped through the
 * shared `ResolveAndImportDialog` shell. Reviewing phase shows server-
 * resolved identity per email (existing user / existing contact /
 * already a member / already invited / no match) plus prior-event
 * affiliations.
 *
 * Replaces the prior in-dialog snapshot-based classification (which
 * read a client-side persons snapshot and could race with cross-tab
 * person creation). Server-side `bulkResolveAttendees` does the lookup
 * with current Firestore state per request, surfaces ADR-057 prior-event
 * context, and removes the `excludedEmails` prop the parent had to
 * compute.
 *
 * Write path stays on `bulkInviteToEvent` — that callable was already
 * the right granularity (single round-trip via BulkWriter) and only its
 * input shape (an email list) is needed.
 */
export function ExcelAttendeeImportDialog({
  open,
  onClose,
  eventId,
  onImported,
}: ExcelAttendeeImportDialogProps) {
  const resolveRows = useCallback(
    async (rows: ParsedAttendeeImportRow[]) => {
      const response = await bulkResolveAttendees({
        eventId,
        emails: rows.map((r) => r.email),
      });
      return response.results;
    },
    [eventId],
  );

  const writeRows = useCallback(
    async (rows: ParsedAttendeeImportRow[]) => {
      return bulkInviteToEvent({
        eventId,
        emails: rows.map((r) => r.email),
      });
    },
    [eventId],
  );

  const renderProposalCard = useCallback(
    (proposal: AttendeeProposal, onToggle: () => void) => (
      <ProposalCard proposal={proposal} onToggle={onToggle} />
    ),
    [],
  );

  return (
    <ResolveAndImportDialog<
      ParsedAttendeeImportRow,
      BulkResolveAttendeesRowResult,
      WriteResult
    >
      open={open}
      onClose={onClose}
      onImported={onImported}
      title="Import Roster from Excel"
      entityNoun={{ singular: "Attendee", plural: "Attendees" }}
      pickerHelpText={
        <>
          Upload the <strong>Roster</strong> sheet from the canonical
          Planning Workbook, or any .xlsx with one email per row. Each
          email is resolved server-side against the global users +
          contacts list; you'll see who each email matches (and whether
          they're already on this event) before anything is saved. No
          emails are sent on add — use the Send Emails action on the
          Attendees page when you're ready.
        </>
      }
      testIdSlug="attendee"
      spec={attendeeImportSpec}
      templateFileName="roster-import-template.xlsx"
      sheetName="Roster"
      resolveRows={resolveRows}
      writeRows={writeRows}
      defaultIncludeForResolution={(resolution) =>
        defaultIncludeForAttendeeStatus(resolution?.status ?? "invalid")
      }
      renderProposalCard={renderProposalCard}
      buildResultBuckets={({ writeResult, includedProposals, skippedProposals }) =>
        buildAttendeeBuckets(writeResult, includedProposals, skippedProposals)
      }
      importedAnythingFromResult={(result) => result.summary.invited > 0}
    />
  );
}

// ---------------------------------------------------------------------------
// Per-row proposal card (one chip per email)
// ---------------------------------------------------------------------------

function ProposalCard({
  proposal,
  onToggle,
}: {
  proposal: AttendeeProposal;
  onToggle: () => void;
}) {
  const { resolution, parsed, include, includeLocked, lineNumber } = proposal;

  if (!resolution) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 1.5 }}
        data-testid={`proposal-row-${lineNumber}`}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox
            checked={false}
            disabled
            data-testid={`proposal-include-${lineNumber}`}
          />
          <Stack spacing={0.5} flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={500}>
              Row {lineNumber}: {parsed.data.email || <em>(missing email)</em>}
            </Typography>
            <Typography variant="caption" color="error.main">
              {parsed.message ?? "Format error"}
            </Typography>
          </Stack>
          <Chip size="small" label="Format error" color="error" />
        </Stack>
      </Paper>
    );
  }

  const proposalDisplay = resolution.proposal?.displayName ?? resolution.email;
  const tooltipParts: string[] = [resolution.email];
  if (resolution.proposal?.priorEventNames.length) {
    tooltipParts.push(
      `Prior events: ${resolution.proposal.priorEventNames.join(", ")}`,
    );
  }
  if (resolution.status === "already_invited") {
    tooltipParts.push("Pending invitation already sent for this event.");
  } else if (resolution.status === "already_member") {
    tooltipParts.push("Already an active member of this event.");
  } else if (resolution.status === "not_found") {
    tooltipParts.push(
      "Not in users or contacts. Add via the Users page first.",
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, opacity: includeLocked ? 0.85 : 1 }}
      data-testid={`proposal-row-${lineNumber}`}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Checkbox
          checked={include}
          disabled={includeLocked}
          onChange={onToggle}
          data-testid={`proposal-include-${lineNumber}`}
        />
        <Tooltip
          title={
            <Box sx={{ whiteSpace: "pre-line" }}>{tooltipParts.join("\n")}</Box>
          }
        >
          <Stack spacing={0.5} flex={1} minWidth={0}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              {resolution.proposal?.photoURL && (
                <Avatar
                  src={resolution.proposal.photoURL}
                  sx={{ width: 24, height: 24 }}
                />
              )}
              <Typography variant="body2" fontWeight={500}>
                Row {lineNumber}: {proposalDisplay}
              </Typography>
              <StatusChip status={resolution.status} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {resolution.email}
            </Typography>
            {resolution.proposal?.priorEventNames.length ? (
              <Typography variant="caption" color="text.secondary">
                Prior events:{" "}
                {resolution.proposal.priorEventNames.join(", ")}
              </Typography>
            ) : null}
          </Stack>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

function StatusChip({ status }: { status: BulkResolveAttendeesRowStatus }) {
  switch (status) {
    case "new":
      return <Chip size="small" label="New, ready" color="success" />;
    case "already_member":
      return <Chip size="small" label="Already on event" color="default" />;
    case "already_invited":
      return <Chip size="small" label="Already invited" color="default" />;
    case "not_found":
      return <Chip size="small" label="Not in directory" color="warning" />;
    case "failed":
      return <Chip size="small" label="Failed" color="error" />;
  }
}

// ---------------------------------------------------------------------------
// Done-phase result bucketing
// ---------------------------------------------------------------------------

interface ResultRow {
  lineNumber: number;
  email: string;
  displayName: string | null;
  status: "added" | "not_found" | "already_on_event" | "skipped" | "failed";
  message: string | null;
}

function buildAttendeeBuckets(
  writeResult: WriteResult | null,
  includedProposals: AttendeeProposal[],
  skippedProposals: AttendeeProposal[],
): BulkImportResultBucket[] {
  // bulkInviteToEvent normalizes + dedupes server-side; results come back
  // keyed by lowercased email. Match each included proposal to the
  // corresponding response row.
  const responseByEmail = new Map<string, WriteResult["results"][number]>();
  if (writeResult) {
    for (const r of writeResult.results) {
      responseByEmail.set(r.email.toLowerCase(), r);
    }
  }

  const writeRows: ResultRow[] = includedProposals.map((p) => {
    const norm = p.resolution?.normalizedEmail ?? p.parsed.data.email;
    const r = responseByEmail.get(norm);
    if (!r) {
      return {
        lineNumber: p.lineNumber,
        email: p.parsed.data.email,
        displayName: p.resolution?.proposal?.displayName ?? null,
        status: "failed" as const,
        message: "Skipped at write phase",
      };
    }
    if (r.status === "invited") {
      return {
        lineNumber: p.lineNumber,
        email: p.parsed.data.email,
        displayName: p.resolution?.proposal?.displayName ?? null,
        status: "added" as const,
        message: null,
      };
    }
    if (r.status === "already_member") {
      return {
        lineNumber: p.lineNumber,
        email: p.parsed.data.email,
        displayName: p.resolution?.proposal?.displayName ?? null,
        status: "already_on_event" as const,
        message: null,
      };
    }
    return {
      lineNumber: p.lineNumber,
      email: p.parsed.data.email,
      displayName: p.resolution?.proposal?.displayName ?? null,
      status: "failed" as const,
      message: r.error ?? r.status,
    };
  });

  const skipRows: ResultRow[] = skippedProposals.map((p) => {
    if (!p.resolution) {
      return {
        lineNumber: p.lineNumber,
        email: p.parsed.data.email || "(invalid)",
        displayName: null,
        status: "skipped" as const,
        message: p.parsed.message ?? "Format error",
      };
    }
    if (p.resolution.status === "already_member") {
      return {
        lineNumber: p.lineNumber,
        email: p.resolution.email,
        displayName: p.resolution.proposal?.displayName ?? null,
        status: "already_on_event" as const,
        message: null,
      };
    }
    if (p.resolution.status === "already_invited") {
      return {
        lineNumber: p.lineNumber,
        email: p.resolution.email,
        displayName: p.resolution.proposal?.displayName ?? null,
        status: "already_on_event" as const,
        message: "Pending invitation",
      };
    }
    if (p.resolution.status === "not_found") {
      return {
        lineNumber: p.lineNumber,
        email: p.resolution.email,
        displayName: null,
        status: "not_found" as const,
        message: null,
      };
    }
    return {
      lineNumber: p.lineNumber,
      email: p.resolution.email,
      displayName: p.resolution.proposal?.displayName ?? null,
      status: "skipped" as const,
      message: p.resolution.message ?? null,
    };
  });

  const all = [...writeRows, ...skipRows].sort(
    (a, b) => a.lineNumber - b.lineNumber,
  );

  const added = all.filter((r) => r.status === "added");
  const notFound = all.filter((r) => r.status === "not_found");
  const alreadyOnEvent = all.filter((r) => r.status === "already_on_event");
  const skipped = all.filter((r) => r.status === "skipped");
  const failed = all.filter((r) => r.status === "failed");

  return [
    {
      id: "added",
      label: "Successfully Added",
      color: "success",
      rows: added.map((r) => ({
        id: `a-${r.lineNumber}`,
        primary: r.displayName ?? r.email,
        secondary: r.email,
      })),
    },
    {
      id: "not-found",
      label: "Users Not Found",
      color: "warning",
      description:
        "These emails aren't in the users or contacts collections yet. Add them via the Users page first, then re-import.",
      rows: notFound.map((r) => ({
        id: `n-${r.lineNumber}`,
        primary: r.email,
        secondary: "Not in directory",
      })),
    },
    {
      id: "already",
      label: "Already In Event",
      color: "default",
      rows: alreadyOnEvent.map((r) => ({
        id: `e-${r.lineNumber}`,
        primary: r.displayName ?? r.email,
        secondary: r.message ? `${r.email} — ${r.message}` : r.email,
      })),
    },
    {
      id: "skipped",
      label: "Skipped",
      color: "default",
      rows: skipped.map((r) => ({
        id: `k-${r.lineNumber}`,
        primary: r.email,
        secondary: r.message ?? undefined,
      })),
    },
    {
      id: "failed",
      label: "Failed",
      color: "error",
      description: "Validation errors or callable rejections.",
      rows: failed.map((r) => ({
        id: `f-${r.lineNumber}`,
        primary: r.email,
        secondary: `Row ${r.lineNumber}: ${r.message ?? "unknown error"}`,
      })),
    },
  ];
}
